import clsx from "clsx";
import { useRouter } from "next/router";
import { useCallback, useRef, useState } from "react";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import parse from "html-react-parser";
import WSlider from "../wslider/WSlider";
import { Enum_Componentcmsdataslider_Type } from "@/generated";
import { createPortal } from "react-dom";
import WModal from "../wmodal/WModal";
import Image from "next/image";
/* eslint-disable-next-line */
export interface LCardsSliderProps {
  id?: string;
  title?: string;
  subTitle?: string;
  centerText?: boolean;
  popup?: boolean | null;
  popupRichText?: string | null;
  type?: Enum_Componentcmsdataslider_Type | null;
  cards: {
    titleNrOfChars?: number | null;
    underTextNrOfChars?: number | null;
    bodyNrOfChars?: number | null;
    image: {
      imageUrl: string;
      altTag: string;
    };
    title?: string;
    description?: string;
    url?: string;
    isOnLanding?: boolean;
    subtitleOverImage?: boolean;
    underText?: string;
    frameColor?: string;
    titleColor?: string;
    bodyColor?: string;
    underTextColor?: string;
  }[];
}

export function LCardsSlider(props: LCardsSliderProps) {
  const router = useRouter();
  const [modal, setModal] = useState("");
  const slider = useRef<HTMLDivElement>(null);

  const scrollSlider = useCallback((direction: string) => {
    slider?.current?.scrollBy({
      left: direction === "left" ? -500 : 500,
      behavior: "smooth",
    });
  }, []);

  return (
    <>
      <div className="py-10 bg-eklight" id={props?.id}>
        <div className="flex flex-col gap-5 px-5">
          {(props?.title || props?.subTitle) && (
            <div className={clsx([props?.centerText && "text-center"])}>
              {props?.title && <h2>{parse(props?.title)}</h2>}
              {props?.subTitle && (
                <div className="text-lg lg:text-2xl">{props?.subTitle}</div>
              )}
            </div>
          )}

          <WSlider>
            {props?.cards?.map((card, idx) => (
              <div
                key={idx}
                className={clsx([
                  !card?.subtitleOverImage && "m-2 rounded-md",
                  props?.type === "circle"
                    ? "bg-transparent p-2"
                    : "bg-white shadow-sm",
                  "flex flex-col gap-2",
                  props?.type === "wide"
                    ? "w-96 lg:w-128"
                    : props?.type === "circle"
                      ? "w-32 lg:w-64"
                      : "w-48 lg:w-64",
                ])}
              >
                <div
                  onClick={() => {
                    card?.url && router?.push(card?.url);
                    props?.popup && setModal(`showModal${idx}`);
                  }}
                  className={clsx([
                    !card?.subtitleOverImage &&
                      props?.type !== "circle" &&
                      "rounded-bl-none rounded-br-none",
                    "relative flex-shrink-0 overflow-hidden transition-all cursor-pointer snap-start",
                    props?.type === "circle" &&
                      "rounded-full aspect-square p-1 lg:p-2",
                    props?.type === "square" && "rounded-lg",
                    props?.type === "wide" && "rounded-lg",
                    props?.type === "wide"
                      ? "w-96 lg:w-128"
                      : props?.type === "circle"
                        ? "w-32 lg:w-64"
                        : "w-48 lg:w-64",
                    props?.type === "circle" && "shadow-md",
                  ])}
                  style={{
                    backgroundColor:
                      props?.type === "circle" ? card?.frameColor : "white",
                  }}
                >
                  <Image
                    src={card?.image?.imageUrl}
                    className={clsx([
                      "object-cover transition-all hover:scale-105 w-full h-full",
                      props?.type === "circle" && card?.frameColor
                        ? "rounded-full"
                        : props?.type !== "wide" &&
                          "rounded-full ring-2 ring-white",
                      props?.type === "square" && "rounded-lg",
                      props?.type === "wide" ? "object-center" : "object-top",
                    ])}
                    width={500}
                    height={300}
                    alt={card?.image?.altTag || "image"}
                    quality={80}
                  />

                  <div
                    className={clsx([
                      !card?.subtitleOverImage &&
                        "rounded-bl-none rounded-br-none",
                      props?.type !== "circle" &&
                        "bg-gradient-to-t from-black/75 to-transparent",
                      props?.type === "circle" && "relative",
                      "absolute bottom-0 w-full pt-20 pb-3 text-center text-white pointer-events-none ",
                      props?.type === "square" && "rounded-lg",
                    ])}
                  >
                    {card?.title && (
                      <div
                        style={{ color: card?.titleColor || "#fff" }}
                        className="text-sm font-bold"
                      >
                        {card?.title?.length > (card?.titleNrOfChars || 0)
                          ? `${card?.title?.substring(0, card?.titleNrOfChars || 0)}...`
                          : card?.title}
                      </div>
                    )}

                    {card?.description && card?.subtitleOverImage && (
                      <div
                        style={{ color: card?.bodyColor || "white" }}
                        className="overflow-hidden"
                      >
                        {card?.description?.length >= (card?.bodyNrOfChars || 0)
                          ? `${card?.description?.substring(0, card?.bodyNrOfChars || 0)}...`
                          : card?.description}
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={clsx([
                    props?.type === "circle" && " rounded-md text-center",
                  ])}
                >
                  {card?.title &&
                    !card?.subtitleOverImage &&
                    props?.type === "circle" && (
                      <div
                        style={{ color: card?.titleColor }}
                        className="text-xl lg:text-2xl font-bold pl-2 pt-2"
                      >
                        {card?.title?.length > (card?.titleNrOfChars || 0)
                          ? `${card?.title?.substring(0, card?.titleNrOfChars || 0)}...`
                          : card?.title}
                      </div>
                    )}
                  {card?.description && !card?.subtitleOverImage && (
                    <div className={clsx(["flex flex-col gap-2 p-2 ", ,])}>
                      <div
                        style={{ color: card?.bodyColor }}
                        className="text-sm font-bold"
                      >
                        <ReactMarkdown
                          className="whitespace-pre-wrap"
                          // disable es-lint because react-markedown decided its a "good" idea to call the prop "children"
                          // eslint-disable-next-line react/no-children-prop
                          children={
                            card?.description?.length >=
                            (card?.bodyNrOfChars || 0)
                              ? `${card?.description?.substring(0, card?.bodyNrOfChars || 0)}...`
                              : card?.description
                          }
                        />
                      </div>
                      {card?.underText && (
                        <div
                          style={{ color: card?.underTextColor }}
                          className="small-markdown"
                        >
                          <ReactMarkdown
                            // disable es-lint because react-markedown decided its a "good" idea to call the prop "children"
                            // eslint-disable-next-line react/no-children-prop
                            children={
                              card?.underText?.length >
                              (card?.underTextNrOfChars || 0)
                                ? `${card?.underText?.substring(0, card?.underTextNrOfChars || 0)}...`
                                : card?.underText
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {modal === `showModal${idx}` && (
                  <WModal large setModal={setModal}>
                    <div className="flex gap-5">
                      <div className="flex-shrink-0 hidden lg:block w-72 aspect-square overflow-hidden p-2">
                        <Image
                          src={card?.image?.imageUrl}
                          className={clsx([
                            "object-cover transition-all hover:scale-105 w-full h-full",
                            props?.type === "circle" && card?.frameColor
                              ? "rounded-full"
                              : props?.type !== "wide" &&
                                "rounded-full ring-2 ring-white",
                            props?.type === "square" && "rounded-lg",
                            props?.type === "wide"
                              ? "object-center"
                              : "object-top",
                          ])}
                          alt={card?.image?.altTag || "image"}
                          width={500}
                          height={300}
                          quality={80}
                        />
                      </div>
                      <div className="flex flex-col gap-3">
                        {card?.title && (
                          <div
                            className="text-xl lg:text-2xl font-bold pl-2 pt-2"
                            style={{ color: card?.titleColor || "#fff" }}
                          >
                            {card?.title}
                          </div>
                        )}

                        {card?.description && !card?.subtitleOverImage && (
                          <div
                            className={clsx(["flex flex-col gap-2 p-2 ", ,])}
                          >
                            <div
                              style={{ color: card?.bodyColor }}
                              className="text-sm font-bold"
                            >
                              <ReactMarkdown
                                // disable es-lint because react-markedown decided its a "good" idea to call the prop "children"
                                // eslint-disable-next-line react/no-children-prop
                                children={card?.description}
                              />
                            </div>
                            {card?.underText && (
                              <div
                                style={{ color: card?.underTextColor }}
                                className="small-markdown"
                              >
                                <ReactMarkdown
                                  // disable es-lint because react-markedown decided its a "good" idea to call the prop "children"
                                  // eslint-disable-next-line react/no-children-prop
                                  children={card?.underText}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </WModal>
                )}
              </div>
            ))}
          </WSlider>
        </div>
      </div>
    </>
  );
}

export default LCardsSlider;
